package com.xtream.composeapp.presentation.ui.search

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import androidx.paging.PagingData
import androidx.paging.cachedIn
import com.xtream.data_kmp.db.repo.LiveStreamsRepository
import com.xtream.data_kmp.db.repo.SeriesStreamsRepository
import com.xtream.data_kmp.db.repo.VodStreamsRepository
import com.xtream.data_kmp.dto.LiveCategory
import com.xtream.data_kmp.dto.SeriesCategory
import com.xtream.data_kmp.dto.VodCategory
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update

class SearchScreenViewModel(
    private val liveStreamsRepository: LiveStreamsRepository,
    private val vodStreamsRepository: VodStreamsRepository,
    private val seriesStreamsRepository: SeriesStreamsRepository,
    ): ViewModel() {
    /**
     * gets Movies Categories as Flow<PagingData<VodCategory>>
     * @return Flow<PagingData<VodCategory>>
     */
    val liveCategoriesPagingFlow : Flow<PagingData<LiveCategory>> = liveStreamsRepository.categoriesPagingFlow.cachedIn(viewModelScope)
    val vodCategoriesPagingFlow : Flow<PagingData<VodCategory>> = vodStreamsRepository.categoriesPagingFlow.cachedIn(viewModelScope)
    val seriesCategoriesPagingFlow : Flow<PagingData<SeriesCategory>> = seriesStreamsRepository.categoriesPagingFlow.cachedIn(viewModelScope)

    private val _uiConfigState = MutableStateFlow(SearchUiConfigState())
    val uiConfigState: StateFlow<SearchUiConfigState> = _uiConfigState.asStateFlow()
    val searchQuery= uiConfigState
    .map { it.query.orEmpty() } .distinctUntilChanged()
    val selectedLiveCategoryFlow = uiConfigState.map { it.liveTvConfig.selectedCategories }

    val selectedVodCategoryFlow = uiConfigState.map { it.movieConfig.selectedCategories }
    val selectedSeriesCategoryFlow = uiConfigState.map { it.seriesConfig.selectedCategories }


    private val debouncedQueryFlow = _uiConfigState
        .map { it.query }
        .distinctUntilChanged()
        .debounce(300L)

    val liveStreamsFilteredPagingFlow =
        combine(
            debouncedQueryFlow,
            _uiConfigState.map { uiState -> uiState.liveTvConfig }.distinctUntilChanged()
        ) { keyWord, liveTvConfig ->
             keyWord to liveTvConfig
        }
            .distinctUntilChanged()
            .debounce(300L)

            .flatMapLatest { (keyWord, liveTvConfig) ->
                val categories = liveTvConfig.selectedCategories?.mapNotNull { it.categoryId }
                val sortBy = liveTvConfig.sortBy

                liveStreamsRepository.getPagedLiveStreamsFlow(categoryIds = categories, keyWord, sortBy)
            }
            .cachedIn(viewModelScope)





    val moviesFilteredPagingFlow =
        combine(
            debouncedQueryFlow,
            _uiConfigState.map { it.movieConfig }.distinctUntilChanged()
        ) { keyWord, movieConfig ->
            keyWord to movieConfig
        }
            .distinctUntilChanged()
            .debounce(300L)

            .flatMapLatest { (keyWord, movieConfig) ->
                val categories = movieConfig.selectedCategories?.mapNotNull { it.categoryId }
                val sortBy = movieConfig.sortBy
                val yearRange = movieConfig.range.toUnixRange()

                vodStreamsRepository.getPagedVodStreamsFlow(categoryIds = categories, keyWord, sortBy, yearRange)
            }
            .cachedIn(viewModelScope)

    val seriesFilteredPagingFlow =
        combine(
            debouncedQueryFlow,
            _uiConfigState.map { it.seriesConfig }.distinctUntilChanged()
        ) { keyWord, seriesConfig ->
            keyWord to seriesConfig
        }
            .distinctUntilChanged()
            .debounce(300L)
            .flatMapLatest { (keyWord, seriesConfig) ->
                val categories = seriesConfig.selectedCategories?.mapNotNull { it.categoryId }
                val sortBy = seriesConfig.sortBy
                val yearRange = seriesConfig.range.toUnixRange()

                seriesStreamsRepository.getPagedSeriesStreamsFlow(categoryIds = categories, keyWord, sortBy, yearRange=yearRange)
            }
            .cachedIn(viewModelScope)


    // 1. The Single Source of Truth for your UI Configurations


    /* =========================================================================
       STATE UPDATE FUNCTIONS (Called by your UI Composable)
    ========================================================================= */

    fun onQueryChanged(newQuery: String) {
        _uiConfigState.update { currentState ->
            currentState.copy(query = newQuery)
        }
    }

    fun resetUiState(){
        _uiConfigState.update {
            SearchUiConfigState(query = it.query)
        }
    }
    fun updateLiveConfig(reducer: (SearchLiveState) -> SearchLiveState) {
        _uiConfigState.update { currentState ->
            currentState.copy(
                liveTvConfig = reducer(currentState.liveTvConfig)
            )
        }
    }
    fun updateMovieConfig(reducer: (SearchMovieState) -> SearchMovieState) {
        _uiConfigState.update { currentState ->
            currentState.copy(movieConfig = reducer(currentState.movieConfig))
        }
    }

    fun updateSeriesConfig(reducer: (SearchSeriesState) -> SearchSeriesState) {
        _uiConfigState.update { currentState ->
            currentState.copy(seriesConfig = reducer(currentState.seriesConfig))
        }
    }
    fun updateMovieYearRange(newRange: IntRange) {
        _uiConfigState.update { currentState ->
            currentState.copy(
                movieConfig = currentState.movieConfig.copy(range = newRange)
            )
        }
    }
/*
    fun toggleMovieCategory(categoryId: Int) {
        _uiConfigState.update { currentState ->
            val currentCategories = currentState.movieConfig.selectedCategories
            val updatedCategories = if (currentCategories.contains(categoryId)) {
                currentCategories - categoryId // Remove if already selected
            } else {
                currentCategories + categoryId // Add if not selected
            }

            currentState.copy(
                movieConfig = currentState.movieConfig.copy(selectedCategories = updatedCategories)
            )
        }
    }*/


}